/**
 * Hook for handling API errors with i18n translation and form field mapping.
 *
 * This hook provides utilities for:
 * 1. Translating error codes to user-friendly messages
 * 2. Mapping field errors to React Hook Form
 * 3. Displaying toast notifications with error_id for debugging
 *
 * Usage:
 *   const { handleError, handleMutationError } = useApiError();
 *
 *   // In a mutation's onError callback:
 *   const mutation = useMutation({
 *     mutationFn: createItem,
 *     onError: (error) => handleMutationError(error, form.setError),
 *   });
 *
 *   // Or in a try/catch:
 *   try {
 *     await mutation.mutateAsync(data);
 *   } catch (error) {
 *     handleError(error);
 *   }
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import {
  getErrorMessage,
  getFieldErrors,
  getErrorId,
  getErrorCode,
  isValidationError,
} from '../utils/errors';
import { wasErrorSurfacedGlobally } from '../utils/globalErrorToasts';

interface UseApiErrorOptions {
  showToast?: boolean;
  includeErrorId?: boolean;
}

export function useApiError(options: UseApiErrorOptions = {}) {
  const { showToast = true, includeErrorId = true } = options;
  const { t } = useTranslation('errors');

  /**
   * Handle an API error with toast notification.
   * Use this for simple error handling without form field mapping.
   *
   * **The toast defers to the global handler.** Almost every caller is the
   * `catch` of a React Query `mutateAsync`, and `MutationCache.onError` has
   * already toasted that failure by the time this runs — so toasting again
   * showed the user the same sentence twice, the second copy carrying a bare
   * `(errorId)` suffix. `wasErrorSurfacedGlobally` is identity-keyed, so this
   * stays quiet ONLY for the exact error the user has already been shown; a
   * bare `apiClient`/`fetch` failure (no `MutationCache` behind it) and a
   * mutation that opted the global handler out with
   * `meta: { suppressGlobalErrorToast: true }` both still toast here. The
   * return value is unaffected either way. See §4 of
   * `docs/platform/error-handling-design.md`.
   */
  const handleError = useCallback(
    (error: unknown, interpolation?: Record<string, string | number>) => {
      const errorMessage = getErrorMessage(error, interpolation);
      const errorId = getErrorId(error);

      if (showToast && !wasErrorSurfacedGlobally(error)) {
        const displayMessage =
          includeErrorId && errorId
            ? `${errorMessage} (${errorId})`
            : errorMessage;
        toast.error(displayMessage);
      }

      return { message: errorMessage, errorId, code: getErrorCode(error) };
    },
    [showToast, includeErrorId]
  );

  /**
   * Handle a mutation error with form field mapping.
   * Automatically maps field errors to React Hook Form fields and shows toast for non-field errors.
   */
  const handleMutationError = useCallback(
    <T extends FieldValues>(
      error: unknown,
      setError?: UseFormSetError<T>,
      interpolation?: Record<string, string | number>
    ) => {
      const errorId = getErrorId(error);

      // Check for field-level errors (both validation errors and single-field errors like DUPLICATE_DOMAIN)
      if (setError) {
        const fieldErrors = getFieldErrors(error);
        if (fieldErrors) {
          // `getFieldErrors` returns null rather than an empty object, so a
          // truthy result always paints at least one field red. This arm
          // therefore never needs a toast of its own — and must not grow one:
          // it used to carry a `!hasSetFieldError` toast that was unreachable
          // for exactly this reason, and re-adding one would be a SECOND copy
          // of the deferral guard in `handleError` below. Two copies drifting
          // apart is the very class this hook was changed to close.
          for (const [field, fieldMessage] of Object.entries(fieldErrors)) {
            setError(field as Path<T>, {
              type: 'server',
              message: fieldMessage,
            });
          }
          return { fieldErrors, errorId, code: getErrorCode(error) };
        }
      }

      // Non-field error - show toast
      return handleError(error, interpolation);
    },
    // `showToast`/`includeErrorId` are not read here any more — the field arm
    // above owns no toast, and the non-field arm delegates to `handleError`,
    // which closes over both.
    [handleError]
  );

  /**
   * Get translated error message without side effects.
   * Useful when you need to display the error in a custom way.
   */
  const getTranslatedError = useCallback(
    (error: unknown, interpolation?: Record<string, string | number>) => {
      return {
        message: getErrorMessage(error, interpolation),
        errorId: getErrorId(error),
        code: getErrorCode(error),
        fieldErrors: getFieldErrors(error),
        isValidation: isValidationError(error),
      };
    },
    []
  );

  return {
    handleError,
    handleMutationError,
    getTranslatedError,
    t, // Expose t for custom error messages
  };
}

export default useApiError;
